import type React from 'react'
import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  useEffect,
  useMemo,
} from 'react'
import { type TrackSession } from '@pitbrain/shared'
import { type LatLng } from 'leaflet'
import { type VideoSession } from '../video/VideoSession'

interface TrackSessionContextProps {
  trackSessions: TrackSession[]
  setTrackSessions: React.Dispatch<React.SetStateAction<TrackSession[]>>
  // toggleSelectedLap: (sessionId: string, i: number) => void
  canApplyStartFinish: (finishLine: LatLng[]) => boolean
  applyStartFinishForAllSessions: (finishLine?: LatLng[]) => void
  error: string | null
  resetError: () => void
  videoSessions: VideoSession[]
  setVideoSessions: React.Dispatch<React.SetStateAction<VideoSession[]>>
}

const TrackSessionContext = createContext<TrackSessionContextProps | undefined>(
  undefined
)

export const useTrackSessionContext = (): TrackSessionContextProps => {
  const context = useContext(TrackSessionContext)
  if (!context) {
    throw new Error(
      'useTrackSession must be used within a TrackSessionProvider'
    )
  }
  return context
}

interface TrackSessionProviderProps {
  children: ReactNode
}

export const TrackSessionProvider: React.FC<TrackSessionProviderProps> = ({
  children,
}) => {
  const [trackSessions, setTrackSessions] = useState<TrackSession[]>([])
  const [finishLine, setFinishLine] = useState<LatLng[]>([])
  const [videoSessions, setVideoSessions] = useState<VideoSession[]>([])
  const [error, setError] = useState<string | null>(null)

  // const toggleSelectedLap = (sessionId: string, i: number) => {
  // }

  const resetError = () => {
    setError(null)
  }

  const hasValidFinishLine = (finishLine?: LatLng[]) =>
    finishLine != null && finishLine.length >= 1
  const hasTrackSessions = () => trackSessions.length > 0
  const hasLatLngs = () =>
    trackSessions[0]?.processedTelemetry?.gpsPoints != null &&
    trackSessions[0]?.processedTelemetry?.gpsPoints.length > 0
  const hasKnownTrack = () => trackSessions[0]?.track?.name !== 'Unknown'
  const hasTrackFinishLine = () => {
    const fl = trackSessions[0]?.track?.finishLine
    return fl != null && fl.length >= 1
  }
  const canApplyStartFinish = (finishLine?: LatLng[]) =>
    hasTrackSessions() &&
    hasLatLngs() &&
    (hasValidFinishLine(finishLine) || hasKnownTrack() || hasTrackFinishLine())

  const applyStartFinishForAllSessions = (inputFinishLine?: LatLng[]) => {
    let internalFinishLine = []
    if (hasValidFinishLine(inputFinishLine) && inputFinishLine != null) {
      setFinishLine(inputFinishLine)
      internalFinishLine = inputFinishLine
    } else {
      internalFinishLine = finishLine
    }
    if (!canApplyStartFinish(internalFinishLine)) {
      return
    }

    const isFirstSession = trackSessions.length === 1

    for (let i = 0; i < trackSessions.length; i++) {
      try {
        trackSessions[i].applyStartFinishForSession(
          i,
          internalFinishLine.map((p) => ({
            latitude: p.lat,
            longitude: p.lng,
          }))
        )

        const isNewUpload = !trackSessions[i].isStored
        const shouldAutoSelect =
          trackSessions[i].laps.length > 0 &&
          (isNewUpload || (trackSessions[i].isStored && isFirstSession))

        if (shouldAutoSelect) {
          // Only select if no laps are already selected (preserves any prior selections)
          const hasSelectedLap = trackSessions[i].laps.some(
            (lap) => lap.isSelected
          )
          if (!hasSelectedLap) {
            trackSessions[i].laps[0].isSelected = true
          }
        }
      } catch (e) {
        const fileName = trackSessions[i].fileName ?? 'session'
        const detail = e instanceof Error ? `: ${e.message}` : ''
        console.error(`Failed to process ${fileName}`, e)
        setError(`Unable to process ${fileName}${detail}`)
        setVideoSessions([
          ...videoSessions.filter((v) => v.id !== trackSessions[i].id),
        ])
        trackSessions.splice(i, 1)
        setTrackSessions([...trackSessions])
        return
      }
    }

    // Ensure at least one lap is selected across all sessions
    const hasAnySelectedLap = trackSessions.some((session) =>
      session.laps.some((lap) => lap.isSelected)
    )

    if (!hasAnySelectedLap) {
      for (const session of trackSessions) {
        if (session.laps.length > 0) {
          session.laps[0].isSelected = true
          break
        }
      }
    }

    setTrackSessions([...trackSessions])
  }

  useEffect(() => {
    try {
      applyStartFinishForAllSessions()
    } catch (e) {
      setError('Unable to process file.')
    }
  }, [trackSessions.length])

  const value = useMemo(
    () => ({
      trackSessions,
      setTrackSessions,
      // toggleSelectedLap,
      canApplyStartFinish,
      applyStartFinishForAllSessions,
      error,
      resetError,
      setVideoSessions,
      videoSessions,
    }),
    [
      trackSessions,
      setTrackSessions,
      // toggleSelectedLap,
      canApplyStartFinish,
      applyStartFinishForAllSessions,
      error,
      resetError,
      setVideoSessions,
      videoSessions,
    ]
  )

  return (
    <TrackSessionContext.Provider value={value}>
      {children}
    </TrackSessionContext.Provider>
  )
}
